(() => {
  if (customElements.get('product-card-details')) return;

  customElements.whenDefined('secondary-media').then(() => {
    if (customElements.get('common-card-media')) return;
    customElements.define('common-card-media', class extends customElements.get('secondary-media') {
      connectedCallback() {
        this.connection = requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.resize = new ResizeObserver(() => {
            this.init();
            if (this.offsetWidth) this.carousel?.resize();
          });
          this.resize.observe(this);
          this.init();
        });
      }

      init() {
        if (this.initialized || !this.isConnected || !this.offsetWidth || !this.template?.content) return;
        this.initialized = true;
        super.init();
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.connection);
        this.resize?.disconnect();
        this.removeEventListener('mousemove', this.onMoveHandler);
        this.removeEventListener('mouseleave', this.onLeaveHandler);
        super.disconnectedCallback();
        this.carousel = null;
        this.initialized = false;
        this.replaceChildren();
      }
    });
  });

  const groups = new WeakMap();

  class CardRowLayout {
    constructor(root) {
      this.root = root;
      this.members = new Set();
      this.observer = new ResizeObserver(() => this.schedule());
    }

    add(details) {
      this.members.add(details);
      this.observer.observe(details.card);
      details.querySelectorAll('.common-product-card__part-content').forEach((content) => this.observer.observe(content));
      this.schedule();
    }

    remove(details) {
      this.members.delete(details);
      this.observer.unobserve(details.card);
      details.querySelectorAll('.common-product-card__part-content').forEach((content) => this.observer.unobserve(content));
      if (!this.members.size) {
        this.observer.disconnect();
        cancelAnimationFrame(this.frame);
        groups.delete(this.root);
      } else this.schedule();
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.align();
      });
    }

    align() {
      const rows = [];
      for (const details of this.members) {
        const card = details.card;
        if (!card.getClientRects().length || !card.offsetWidth) continue;
        const top = card.getBoundingClientRect().top;
        const slots = [...details.children].filter((el) => el.hasAttribute('data-card-part'));
        const order = slots.map((slot) => slot.dataset.cardPart).join(',');
        let row = rows.find((item) => Math.abs(item.top - top) < 2 && item.order === order);
        if (!row) {
          row = { top, order, entries: [], heights: new Map() };
          rows.push(row);
        }
        row.entries.push(slots);
        for (const slot of slots) {
          const height = slot.firstElementChild.getBoundingClientRect().height;
          row.heights.set(slot.dataset.cardPart, Math.max(row.heights.get(slot.dataset.cardPart) || 0, height));
        }
      }
      for (const row of rows) {
        for (const slots of row.entries) {
          for (const slot of slots) {
            const value = `${Math.ceil(row.heights.get(slot.dataset.cardPart))}px`;
            if (slot.style.getPropertyValue('--common-card-part-height') !== value) {
              slot.style.setProperty('--common-card-part-height', value);
            }
          }
        }
      }
    }
  }

  customElements.define('product-card-details', class extends HTMLElement {
    connectedCallback() {
      // Wait until the parser has added the complete card and its contents.
      this.connection = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        const card = this.closest('.common-product-card');
        if (!card) return;
        this.card = card;
        card.querySelectorAll('.common-product-card__price--split .price__regular').forEach((price) => {
          if (price.querySelector('.common-product-card__price-integer')) return;
          const match = price.textContent.trim().match(/^(\D*?)(\d[\d.,\s]*?)([.,]\d{2})?(\D*)$/);
          if (!match) return;
          const parts = ['prefix', 'integer', 'fraction', 'suffix'];
          price.replaceChildren(...match.slice(1).map((value, index) => {
            const span = document.createElement('span');
            span.className = `common-product-card__price-${parts[index]}`;
            span.textContent = value || '';
            return span;
          }));
        });
        this.events?.abort();
        this.events = new AbortController();
        const image = card.querySelector('[data-card-quick-view]');
        const options = { signal: this.events.signal };
        image?.addEventListener('click', (event) => {
          event.preventDefault();
          // Keep carousel pagination and video controls independent of the preview.
          if (event.target.closest('button, input, select, video, iframe, .flickity-page-dots')) return;
          const modal = document.getElementById(image.dataset.cardQuickView);
          if (modal && !modal.hasAttribute('open')) modal.show(image);
        }, options);
        image?.addEventListener('keydown', (event) => {
          if (event.target === image && event.code === 'Space') {
            event.preventDefault();
            image.click();
          }
        }, options);
        const root = card.closest('[data-card-grid]') || card.closest('.bfcm-flash-sale__products') || card.closest('.mmb__card')?.parentElement || card.parentElement;
        if (!groups.has(root)) groups.set(root, new CardRowLayout(root));
        this.layout = groups.get(root);
        this.layout.add(this);
      });
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.connection);
      this.events?.abort();
      this.layout?.remove(this);
      this.layout = null;
    }
  });
})();
